import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { FileXls, ArrowsClockwise, MagnifyingGlass, FilePdf } from '@phosphor-icons/react';

const REPORT_TABS = [
  { key: 'jo_extract', label: 'JO Extract' },
  { key: 'bom', label: 'BOM Report' },
  { key: 'material_req', label: 'Material Requirement' },
  { key: 'stock_movement', label: 'Stock Movement' },
];

function exportCSV(data, columns, filename) {
  const headers = columns.map(c => c.label);
  const rows = data.map(row => columns.map(c => {
    const val = row[c.key];
    if (val === undefined || val === null) return '';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    return String(val).includes(',') ? `"${val}"` : val;
  }));
  const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ═══════ JO EXTRACT REPORT ═══════ */
function JOExtractReport() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? `?jo_number=${encodeURIComponent(filter)}` : '';
      const { data: res } = await api.get(`/api/inventory/reports/jo-extract${params}`);
      setData(res);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load');
    }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetch(); }, [fetch]);

  const columns = [
    { key: 'jo_number', label: 'JO No' },
    { key: 'customer_name', label: 'Customer' },
    { key: 'jo_date', label: 'Date' },
    { key: 'item_name', label: 'Item Name' },
    { key: 'width', label: 'Width(mm)' },
    { key: 'height', label: 'Height(mm)' },
    { key: 'qty', label: 'Qty' },
    { key: 'sqft', label: 'SQFT' },
    { key: 'glass_type', label: 'Glass Type' },
    { key: 'mesh_flag', label: 'Mesh' },
    { key: 'profile', label: 'Profile' },
  ];

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-end flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-bold text-zinc-500 uppercase">Filter by JO Number</label>
          <Input value={filter} onChange={e => setFilter(e.target.value)} placeholder="JO Number" className="h-8 mt-1 text-sm" />
        </div>
        <Button onClick={fetch} size="sm" className="h-8 bg-blue-600 text-white"><MagnifyingGlass size={14} className="mr-1" /> Search</Button>
        <Button onClick={() => exportCSV(data, columns, 'jo_extract_report')} size="sm" variant="outline" className="h-8">
          <FileXls size={14} className="mr-1" /> Excel
        </Button>
      </div>
      <div className="text-xs text-zinc-500">{data.length} rows</div>
      {loading ? <p className="p-4 text-zinc-400">Loading...</p> : (
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto border rounded">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-zinc-50 z-10">
              <tr className="border-b">
                {columns.map(c => <th key={c.key} className="p-1.5 text-left font-bold uppercase text-zinc-500 whitespace-nowrap">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr key={`${row.jo_number}-${row.item_name}-${idx}`} className="border-b hover:bg-zinc-50">
                  {columns.map(c => (
                    <td key={c.key} className="p-1.5 whitespace-nowrap">
                      {c.key === 'mesh_flag' ? (row[c.key] ? 'Yes' : 'No') : (row[c.key] ?? '--')}
                    </td>
                  ))}
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={columns.length} className="p-6 text-center text-zinc-400">No data</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ═══════ BOM REPORT ═══════ */
function BOMReport() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? `?jo_number=${encodeURIComponent(filter)}` : '';
      const { data: res } = await api.get(`/api/inventory/reports/bom${params}`);
      setData(res);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load');
    }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetch(); }, [fetch]);

  const columns = [
    { key: 'jo_number', label: 'JO No' },
    { key: 'item_name', label: 'Item Name' },
    { key: 'material_name', label: 'Material' },
    { key: 'category', label: 'Category' },
    { key: 'uqc', label: 'UQC' },
    { key: 'item_sqft', label: 'Item SQFT' },
    { key: 'adjusted_sqft', label: 'Adj SQFT' },
    { key: 'net_qty', label: 'Net Qty' },
    { key: 'wastage_pct', label: 'Wastage%' },
    { key: 'gross_qty', label: 'Gross Qty' },
  ];

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-end flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-bold text-zinc-500 uppercase">Filter by JO Number</label>
          <Input value={filter} onChange={e => setFilter(e.target.value)} placeholder="JO Number" className="h-8 mt-1 text-sm" />
        </div>
        <Button onClick={fetch} size="sm" className="h-8 bg-blue-600 text-white"><MagnifyingGlass size={14} className="mr-1" /> Search</Button>
        <Button onClick={() => exportCSV(data, columns, 'bom_report')} size="sm" variant="outline" className="h-8">
          <FileXls size={14} className="mr-1" /> Excel
        </Button>
      </div>
      <div className="text-xs text-zinc-500">{data.length} rows</div>
      {loading ? <p className="p-4 text-zinc-400">Loading...</p> : (
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto border rounded">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-zinc-50 z-10">
              <tr className="border-b">
                {columns.map(c => <th key={c.key} className="p-1.5 text-left font-bold uppercase text-zinc-500 whitespace-nowrap">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr key={`${row.jo_number}-${row.material_name}-${idx}`} className="border-b hover:bg-zinc-50">
                  {columns.map(c => (
                    <td key={c.key} className="p-1.5 whitespace-nowrap">
                      {typeof row[c.key] === 'number' ? row[c.key].toFixed?.(2) ?? row[c.key] : (row[c.key] ?? '--')}
                    </td>
                  ))}
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={columns.length} className="p-6 text-center text-zinc-400">No data</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ═══════ MATERIAL REQUIREMENT REPORT ═══════ */
function MaterialRequirementReport() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/api/inventory/reports/material-requirement');
      setData(res);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load');
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const columns = [
    { key: 'material_name', label: 'Material Name' },
    { key: 'category', label: 'Category' },
    { key: 'uqc', label: 'UQC' },
    { key: 'total_required', label: 'Total Required' },
    { key: 'available_stock', label: 'Available Stock' },
    { key: 'reserved_stock', label: 'Reserved Stock' },
    { key: 'current_stock', label: 'Closing Stock' },
    { key: 'shortage', label: 'Shortage' },
  ];

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center">
        <Button onClick={fetch} size="sm" className="h-8 bg-blue-600 text-white"><ArrowsClockwise size={14} className="mr-1" /> Refresh</Button>
        <Button onClick={() => exportCSV(data, columns, 'material_requirement_report')} size="sm" variant="outline" className="h-8">
          <FileXls size={14} className="mr-1" /> Excel
        </Button>
        <div className="text-xs text-zinc-500">{data.length} materials</div>
      </div>
      {loading ? <p className="p-4 text-zinc-400">Loading...</p> : (
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto border rounded">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-zinc-50 z-10">
              <tr className="border-b">
                {columns.map(c => <th key={c.key} className="p-1.5 text-left font-bold uppercase text-zinc-500 whitespace-nowrap">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => {
                const hasShortage = (row.shortage || 0) > 0;
                return (
                  <tr key={row.material_name} className={`border-b hover:bg-zinc-50 ${hasShortage ? 'bg-red-50' : ''}`}>
                    <td className="p-1.5 font-bold text-zinc-800">{row.material_name}</td>
                    <td className="p-1.5">{row.category}</td>
                    <td className="p-1.5 font-bold">{row.uqc}</td>
                    <td className="p-1.5 text-blue-700 font-bold">{row.total_required?.toFixed(2)}</td>
                    <td className={`p-1.5 font-bold ${hasShortage ? 'text-red-600' : 'text-emerald-600'}`}>{row.available_stock?.toFixed(2)}</td>
                    <td className="p-1.5 text-amber-600">{row.reserved_stock}</td>
                    <td className="p-1.5 font-bold">{row.current_stock}</td>
                    <td className={`p-1.5 font-bold ${hasShortage ? 'text-red-600' : 'text-emerald-600'}`}>
                      {hasShortage ? row.shortage.toFixed(2) : 'OK'}
                    </td>
                  </tr>
                );
              })}
              {data.length === 0 && <tr><td colSpan={columns.length} className="p-6 text-center text-zinc-400">No BOM data available. Generate BOM for JOs first.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ═══════ STOCK MOVEMENT REPORT (in Reports) ═══════ */
function StockMovementReportTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const { data: res } = await api.get('/api/inventory/reports/stock-movement');
      setData(res);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load');
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const columns = [
    { key: 'material_name', label: 'Material' },
    { key: 'category', label: 'Category' },
    { key: 'uqc', label: 'UQC' },
    { key: 'opening_stock', label: 'Opening' },
    { key: 'inward', label: 'Inward' },
    { key: 'issued', label: 'Issued' },
    { key: 'returned', label: 'Returned' },
    { key: 'wastage', label: 'Wastage' },
    { key: 'closing_stock', label: 'Closing' },
    { key: 'reserved_stock', label: 'Reserved' },
    { key: 'available_stock', label: 'Available' },
  ];

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center">
        <Button onClick={fetch} size="sm" className="h-8 bg-blue-600 text-white"><ArrowsClockwise size={14} className="mr-1" /> Refresh</Button>
        <Button onClick={() => exportCSV(data, columns, 'stock_movement_report')} size="sm" variant="outline" className="h-8"><FileXls size={14} className="mr-1" /> Excel</Button>
        <span className="text-xs text-zinc-500">{data.length} materials</span>
      </div>
      {loading ? <p className="p-4 text-zinc-400">Loading...</p> : (
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto border rounded">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-zinc-50 z-10">
              <tr className="border-b">
                <th className="p-1.5 text-left font-bold uppercase text-zinc-500">Material</th>
                <th className="p-1.5 font-bold uppercase text-zinc-500">Cat</th>
                <th className="p-1.5 font-bold uppercase text-zinc-500">UQC</th>
                <th className="p-1.5 font-bold uppercase text-zinc-500">Opening</th>
                <th className="p-1.5 font-bold uppercase text-emerald-600">Inward</th>
                <th className="p-1.5 font-bold uppercase text-red-600">Issued</th>
                <th className="p-1.5 font-bold uppercase text-blue-600">Return</th>
                <th className="p-1.5 font-bold uppercase text-amber-600">Wastage</th>
                <th className="p-1.5 font-bold uppercase text-zinc-800 bg-zinc-100">Closing</th>
                <th className="p-1.5 font-bold uppercase text-orange-600">Reserved</th>
                <th className="p-1.5 font-bold uppercase text-emerald-700">Available</th>
              </tr>
            </thead>
            <tbody>
              {data.map(r => (
                <tr key={r.material_name} className="border-b hover:bg-zinc-50">
                  <td className="p-1.5 font-bold text-zinc-800">{r.material_name}</td>
                  <td className="p-1.5 text-center text-zinc-500">{r.category}</td>
                  <td className="p-1.5 text-center font-bold">{r.uqc}</td>
                  <td className="p-1.5 text-center">{r.opening_stock}</td>
                  <td className="p-1.5 text-center text-emerald-700 font-bold">{r.inward || '--'}</td>
                  <td className="p-1.5 text-center text-red-600 font-bold">{r.issued || '--'}</td>
                  <td className="p-1.5 text-center text-blue-600">{r.returned || '--'}</td>
                  <td className="p-1.5 text-center text-amber-600">{r.wastage || '--'}</td>
                  <td className="p-1.5 text-center font-bold bg-zinc-50">{r.closing_stock}</td>
                  <td className="p-1.5 text-center text-orange-600">{r.reserved_stock}</td>
                  <td className={`p-1.5 text-center font-bold ${r.available_stock < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{r.available_stock}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ═══════ MAIN REPORTS PAGE ═══════ */
export function InventoryReports() {
  const [activeTab, setActiveTab] = useState('jo_extract');

  return (
    <Layout>
      <div className="space-y-4 pb-10">
        <h1 className="text-4xl font-light tracking-tight text-zinc-950">Inventory Reports</h1>
        <p className="text-sm text-zinc-500">View and export inventory data across Job Orders</p>

        <div className="flex gap-1 border-b border-zinc-200 overflow-x-auto">
          {REPORT_TABS.map(tab => (
            <button
              key={tab.key}
              data-testid={`report-tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-bold whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'border-b-2 border-orange-600 text-orange-700 bg-orange-50/50'
                  : 'text-zinc-500 hover:text-zinc-800 hover:bg-zinc-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <Card className="rounded-sm shadow-sm">
          <CardContent className="p-4">
            {activeTab === 'jo_extract' && <JOExtractReport />}
            {activeTab === 'bom' && <BOMReport />}
            {activeTab === 'material_req' && <MaterialRequirementReport />}
            {activeTab === 'stock_movement' && <StockMovementReportTab />}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
